/**
 * Functions and variables for the user permissions page (List User Permissions)
 * and service user management pages.
 */
import { addButtonSpinner, addSpinner, addTableSpinner, removeSpinners } from './sidebar';
import {
  addContentDiv,
  addContentHeader,
  addCopyButton,
  addLoadAccountDetailsButton,
  addRowLoadingAnimation,
  configureTableColumns,
  createAndAddInfoTableWide,
  formatTableChildRow,
} from './main';
import { baseApiAxios } from './api';
import { accountIdPattern } from './accounts';
import { getData } from './databases';
import { initDataTable, exportButtons, resetTableButton, reloadTableButton } from './datatable';

const accountIdCellCallback = (td, cellData) => {
  addCopyButton(td);
  addLoadAccountDetailsButton(td);

  if (!accountIdPattern.test(cellData)) {
    $(td).addClass('portal-danger');
  }
};

/**
 * Functions and variables for the user permissions page (List User Permissions)
 */

export const adminUserPermissionsColumns = [
  { id: 'select' },
  { id: 'account_id_col', name: 'Account Id' },
  { id: 'sum_idc_roles_col', name: '# IDC Roles' },
];

const adminUserPermissionsNavigation = [
  {
    Value: 'roles',
    Name: 'Roles',
    AttributeName: 'role-details-tab',
    UidDataAttribute: 'account_id',
    Default: true,
    Callback: formatUserPermissionsTableRolesTab,
  },
];

/**
 * Initialize the datatable to list all permissions of the selected user.
 */
export function initUserPermissionsTable(tableId) {
  configureTableColumns(tableId, adminUserPermissionsColumns);

  const dt = initDataTable(
    tableId,
    [
      ...exportButtons(),
      resetTableButton(),
      reloadTableButton('Reload User Permissions', loadUserPermissionsTableData, 'Reload user permissions (no-cache)'),
    ],
    [
      {
        // Column 0
        visible: true,
        defaultContent: '',
        orderable: false,
        searchable: false,
        data: null,
        name: 'select',
        class: 'details-control',
        width: '20px',
      },
      {
        // Column 1
        visible: true,
        defaultContent: '',
        orderable: true,
        searchable: true,
        type: 'string',
        data: 'account_id',
        name: 'account_id_col',
        title: 'Account Id',
        createdCell: accountIdCellCallback,
      },
      {
        // Column 2
        visible: true,
        defaultContent: '',
        orderable: true,
        searchable: true,
        type: 'num',
        className: 'text-align-left',
        data: 'roles',
        name: 'sum_idc_roles_col',
        title: '# IDC Roles',
        createdCell: addCopyButton,
        render: data => (data ? data.filter(role => role.principal_type === 'idc_user').length : 0),
      },
    ],
    null,
    {
      order: [[1, 'asc']],
    },
  );

  $('#' + tableId + ' tbody').on('click', 'tr td.details-control', function () {
    const tr = $(this).closest('tr');
    const row = dt.row(tr);
    if (row.child.isShown()) {
      tr.removeClass('details');
      row.child.hide();
    } else {
      tr.addClass('details');
      addRowLoadingAnimation(row);
      formatTableChildRow(row, row.data(), adminUserPermissionsNavigation);
    }
  });
}

/**
 * Load the principals for the selected user via API request.
 *
 * @param {object?} headers
 * @param {boolean?} forceReload
 */
export function loadUserPermissionsTableData(tableId, headers, forceReload) {
  const user_name = $('#user-email')[0].value.split(';')[0];
  if (user_name) {
    addSpinner();
    addTableSpinner();
    getData({
      apiPath: 'users/' + user_name,
      dataAttribute: 'roles',
      tableId: tableId,
      headers: headers,
      forceReload: forceReload,
    }).then(response => {
      if (response && response.data) {
        renderUserStatuses(response.data.user_statuses);
      }
    });
  }
}

/**
 * Render IDC user statuses as badges in the statuses card.
 *
 * @param {object} userStatuses - mapping of IDC account name to status (e.g. {"BMW FPC IAM Identity Center Global Int": "ENABLED"})
 */
function renderUserStatuses(userStatuses) {
  const container = document.getElementById('user-statuses-container');
  const row = document.getElementById('user-statuses-row');
  if (!container || !row) return;

  container.innerHTML = '';

  if (!userStatuses || Object.keys(userStatuses).length === 0) {
    row.style.display = 'none';
    return;
  }

  row.style.display = '';

  Object.entries(userStatuses).forEach(([idcName, status]) => {
    const badgeClass = status === 'ENABLED' ? 'badge-success' : 'badge-danger';
    const item = document.createElement('div');
    item.style.marginBottom = '8px';

    const label = document.createElement('span');
    label.textContent = `${idcName}: `;
    label.style.marginRight = '6px';

    const badge = document.createElement('span');
    badge.className = `badge ${badgeClass}`;
    badge.style.fontSize = '0.9em';
    badge.style.padding = '4px 8px';
    badge.textContent = status;

    item.appendChild(label);
    item.appendChild(badge);
    container.appendChild(item);
  });
}

/**
 * Format the roles tab of each AWS account details row.
 *
 * @param {*} data
 * @param {*} content
 */
function formatUserPermissionsTableRolesTab(data, content) {
  let idcDetailsContainer = addContentDiv(content);

  console.debug('formatUserPermissionsTableRolesTab', data);

  const idcRoleData = {
    ...data,
    roles: data.roles ? data.roles.filter(role => role.principal_type === 'idc_user') : [],
  };

  idcRoleData.roles?.length > 0 && formatUserPermissionsTableIDCRolesTabDetails(idcRoleData, idcDetailsContainer, true);
}

/**
 * Add the role details to the IDC roles tab.
 *
 * @param {*} data
 * @param {*} content
 * @param {*} enabled
 */
function formatUserPermissionsTableIDCRolesTabDetails(data, content, _enabled) {
  const roleInformation = [];
  const detailContainer = addContentDiv(content, 'detailsContent');
  addContentHeader(detailContainer, 'IDC Roles');
  const details = document.createElement('div');
  detailContainer.appendChild(details);

  if (data.roles?.length > 0) {
    data.roles.forEach(role => {
      const roleItem = {
        role_name: role.role_name,
      };
      roleInformation.push(roleItem);
    });

    const roleFields = ['role_name'];
    createAndAddInfoTableWide(roleInformation, roleFields, details, true);
  }
}

/**
 * Functions and variables for service user management pages
 */

/**
 * Update the "User Names" dropdown list with a list of IAM users.
 *
 * @param {object} result
 */
function updateIamUsersDropdown(users) {
  removeSpinners();
  const sel = document.getElementById('target-user');

  users.sort();
  users.forEach(user => {
    console.debug(user);
    const opt = document.createElement('option');
    opt.value = user.UserName;
    opt.innerText = '' + user.UserName;
    const subText = 'created ' + user.CreateDate;
    opt.setAttribute('Arn', user.Arn);
    opt.setAttribute('Path', user.Path);
    opt.setAttribute('CreateDate', user.CreateDate);
    opt.setAttribute('data-subtext', subText);
    sel.appendChild(opt);
  });
  $('.selectpicker').selectpicker('refresh');
}

/**
 * Loads a list of IAM users from the selected AWS account.
 */
export function readIamUsersFromAccount(accountId) {
  // Clear the IAM user dropdown
  const sel = document.getElementById('target-user');
  for (let i = sel.options.length - 1; i >= 0; i--) {
    sel.remove(i);
  }
  $('.selectpicker').selectpicker('refresh');

  // Add fancy spinners to show the user how hard we're working
  addSpinner();
  addButtonSpinner();
  const loading_target_iam_user = document.querySelector('.loading-target-iam-user');
  if (loading_target_iam_user) loading_target_iam_user.classList.add('loading-animation');

  // Collect all IAM users from the target AWS account
  baseApiAxios.get('/accounts/' + accountId + '/iam-users').then(response => {
    updateIamUsersDropdown(response.users);
  });
}
